import * as THREE from "three";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import { applyCosmeticPaint } from "../assets/cosmeticPaint.js";
import { createWorkshopEnvironment } from "../units/UnitViewer.js";
import type { CosmeticView } from "./types.js";
import { createEnclaveTerrace, disposeEnclaveTerrace, whenTerraceReady } from "../scene/baseTheme.js";
import { MarchTrail } from "../world/MarchTrail.js";

type State = "loading" | "ready" | "error";
interface Entry {
  canvas: HTMLCanvasElement;
  cosmetic: CosmeticView;
  root: THREE.Group;
  instances: ModelInstance[];
  terrace: THREE.Group | null;
  restore: (() => void)[];
  visible: boolean;
  dirty: boolean;
  loaded: boolean;
  ready: boolean;
  animate: boolean;
  reduced: boolean;
  rotation: number;
  onState: (state: State) => void;
}

/** One WebGL context for all cards. Static cards draw only on load/resize; the selected card rotates. */
export class CosmeticPreviewPool {
  private readonly renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "low-power" });
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(32, 1, 0.05, 30);
  private readonly environment: THREE.WebGLRenderTarget;
  private readonly entries = new Set<Entry>();
  private readonly stage = new THREE.Group();
  private raf = 0;
  private previous = 0;
  private disposed = false;

  constructor() {
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.95;
    this.scene.background = new THREE.Color("#d6d9c8");
    this.environment = createWorkshopEnvironment(this.renderer);
    this.scene.environment = this.environment.texture;
    this.scene.environmentIntensity = 0.55;
    this.scene.add(new THREE.HemisphereLight(0xe8f3e9, 0xa89675, 1.05));
    const key = new THREE.DirectionalLight(0xffeed4, 1.8);
    key.position.set(-3, 5, 4);
    const rim = new THREE.DirectionalLight(0xb2e5e3, 0.8);
    rim.position.set(3, 2, -3);
    this.scene.add(key, rim, this.stage);
    const plinth = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.48, 0.12, 48), new THREE.MeshStandardMaterial({ color: 0xb5b59e, roughness: 0.7 }));
    plinth.position.y = -0.07;
    const trim = new THREE.Mesh(new THREE.CylinderGeometry(1.42, 1.42, 0.025, 48), new THREE.MeshStandardMaterial({ color: 0x516e65, roughness: 0.5, metalness: 0.4 }));
    trim.position.y = -0.035;
    this.stage.add(plinth, trim);
    document.addEventListener("visibilitychange", this.schedule);
  }

  register(canvas: HTMLCanvasElement, cosmetic: CosmeticView, animate: boolean, reduced: boolean, onState: Entry["onState"]): { remove(): void; setReduced(value: boolean): void } {
    const entry: Entry = { canvas, cosmetic, root: new THREE.Group(), instances: [], terrace: null, restore: [], visible: false, dirty: true, loaded: false, ready: false, animate, reduced, rotation: -0.45, onState };
    this.entries.add(entry);
    const intersection = new IntersectionObserver(([item]) => {
      entry.visible = item?.isIntersecting ?? false;
      entry.dirty = true;
      this.schedule();
    }, { rootMargin: "30px" });
    intersection.observe(canvas);
    const resize = new ResizeObserver(() => { entry.dirty = true; this.schedule(); });
    resize.observe(canvas);
    void this.load(entry);
    return {
      remove: () => {
        intersection.disconnect();
        resize.disconnect();
        this.entries.delete(entry);
        this.release(entry);
      },
      setReduced: (value) => { entry.reduced = value; entry.dirty = true; this.schedule(); },
    };
  }

  private async load(entry: Entry): Promise<void> {
    const { cosmetic } = entry;
    try {
      if (cosmetic.kind === "march_trail") {
        const trail = new MarchTrail(cosmetic.id);
        trail.update(new THREE.Vector3(-1.3, 0.03, -1.3), new THREE.Vector3(1.3, 0.03, 1.3), 0.85, true);
        entry.root.add(trail.group);
        entry.restore.push(() => trail.dispose());
        const bounds = new THREE.Box3().setFromObject(entry.root), center = bounds.getCenter(new THREE.Vector3());
        trail.group.position.sub(new THREE.Vector3(center.x, 0, center.z));
        entry.loaded = true;
        this.schedule();
        return;
      }
      if (!cosmetic.asset) throw new Error("Cosmetic has no 3D asset");
      const isBase = cosmetic.kind === "base_skin";
      const asset = await loadModel(isBase ? "bld.command_nexus" : cosmetic.asset, isBase || cosmetic.asset.startsWith("bld.") ? 2 : undefined);
      if (this.disposed || !this.entries.has(entry)) return;
      const instance = instantiate(asset);
      entry.instances.push(instance);
      entry.root.add(instance.object);
      entry.restore.push(applyCosmeticPaint(instance.object, cosmetic));
      if (isBase) {
        entry.terrace = createEnclaveTerrace(6, cosmetic.id);
        entry.root.add(entry.terrace);
        await whenTerraceReady(entry.terrace);
        if (this.disposed || !this.entries.has(entry)) return;
      }
      const bounds = new THREE.Box3().setFromObject(entry.root);
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      const scale = 2.12 / Math.max(size.x, size.y, size.z, 0.01);
      for (const child of entry.root.children) {
        child.position.sub(new THREE.Vector3(center.x, bounds.min.y, center.z)).multiplyScalar(scale);
        child.scale.multiplyScalar(scale);
      }
      entry.loaded = true;
      this.schedule();
    } catch {
      if (!this.disposed && this.entries.has(entry)) {
        this.release(entry);
        entry.onState("error");
      }
    }
  }

  private readonly schedule = () => {
    if (!this.disposed && !this.raf && !document.hidden) this.raf = requestAnimationFrame(this.draw);
  };

  private readonly draw = (now: number) => {
    this.raf = 0;
    if (this.disposed || document.hidden) return;
    if (now - this.previous < 50) { this.schedule(); return; }
    const dt = Math.min(0.1, (now - this.previous) / 1000);
    this.previous = now;
    let animated = false;
    for (const entry of this.entries) {
      if (!entry.visible || !entry.loaded) continue;
      const moving = entry.animate && !entry.reduced;
      animated ||= moving;
      if (!entry.dirty && !moving) continue;
      if (moving) entry.rotation += dt * 0.28;
      entry.root.rotation.y = entry.rotation;
      const rect = entry.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) continue;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));
      if (entry.canvas.width !== width) entry.canvas.width = width;
      if (entry.canvas.height !== height) entry.canvas.height = height;
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      const bounds = new THREE.Box3().setFromObject(entry.root);
      const target = new THREE.Vector3(0, Math.max(0.25, bounds.max.y * 0.48), 0);
      const outward = new THREE.Vector3(0, entry.cosmetic.kind === "march_trail" ? 1.2 : entry.cosmetic.kind === "base_skin" ? 0.85 : 0.42, 1).normalize();
      const up = new THREE.Vector3(0, outward.z, -outward.y);
      const tangent = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)) * 0.84;
      let distance = 1.49 / (tangent * this.camera.aspect);
      for (const x of [bounds.min.x, bounds.max.x]) for (const y of [bounds.min.y, bounds.max.y]) for (const z of [bounds.min.z, bounds.max.z]) {
        const point = new THREE.Vector3(x, y, z).sub(target);
        const depth = point.dot(outward);
        distance = Math.max(distance, depth + Math.abs(x) / (tangent * this.camera.aspect), depth + Math.abs(point.dot(up)) / tangent);
      }
      this.camera.position.copy(target).addScaledVector(outward, distance);
      this.camera.lookAt(target);
      this.camera.updateProjectionMatrix();
      this.scene.add(entry.root);
      this.renderer.render(this.scene, this.camera);
      this.scene.remove(entry.root);
      const context = entry.canvas.getContext("2d", { alpha: false });
      if (!context) { entry.onState("error"); continue; }
      context.drawImage(this.renderer.domElement, 0, 0);
      entry.dirty = false;
      if (!entry.ready) {
        entry.ready = true;
        entry.canvas.dataset["rendered"] = "true";
        entry.onState("ready");
      }
    }
    if (animated) this.schedule();
  };

  private release(entry: Entry): void {
    for (const restore of entry.restore) restore();
    for (const instance of entry.instances) {
      instance.mixer.stopAllAction();
      instance.mixer.uncacheRoot(instance.object);
    }
    entry.instances = [];
    if (entry.terrace) disposeEnclaveTerrace(entry.terrace);
    entry.terrace = null;
    entry.restore = [];
    entry.root.clear();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    document.removeEventListener("visibilitychange", this.schedule);
    for (const entry of this.entries) this.release(entry);
    this.entries.clear();
    this.stage.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        for (const material of Array.isArray(object.material) ? object.material : [object.material]) material.dispose();
      }
    });
    this.environment.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
